import React, { FunctionComponent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import variantsListSelectors from '../../../../store/selectors/variantsListSelectors';
import carConfigurationSelectors from '../../../../store/selectors/carConfigurationSelectors';
import VariantList from '../../../../../common/components/variantSelector/variantList/VariantList';
import { carConfigurationActions } from '../../../../store/reducers/carConfigurationReducer';
import SectionLoader from '../../../../../common/components/loaders/SectionLoader';
import ThemedErrorMessage from '../../../../../common/components/presentation/error/ThemedErrorMessage';
import SpecificationsAndFeaturesContainer from './specificationsAndFeatures/SpecificationsAndFeaturesContainer';
import thunkActions from '../../../../store/actions/thunkActions';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import IProductVariantList, { IVariantListItems } from '../../../../../common/model/IProductVariantList';
import configurationSourceUrlService from '../../../../service/configurationSourceUrlService';
import { useParams } from 'react-router';
import useWidgetContext from '../../../../../common/react/useWidgetContext';
import { customiseActions } from '../../../../store/reducers/customiseReducer';
import { CustomiseSections } from '../../../../../common/enum/CustomiseSections';
import IParams from '../../../../../gqlCustomise/model/IParams';

interface IVariantListContainerProps {
  selectedMarketingCategory?: string;
}

const VariantListContainer: FunctionComponent<IVariantListContainerProps> = ({ selectedMarketingCategory }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(variantsListSelectors.getVariantsListLoadingStatus);
  const variantsList: IProductVariantList = useSelector(variantsListSelectors.getVariantsList);
  const variantsListError = useSelector(variantsListSelectors.getVariantsListError);
  const variantsItems = useSelector(variantsListSelectors.getVariantListItems);
  const model = useSelector(variantsListSelectors.getVariantListModel);
  const selectedVariantId = useSelector(carConfigurationSelectors.getSelectedVariant);
  const {
    platformConfig: { priceDisplay },
  } = usePlatformConfig();
  const { modelId } = useParams<IParams>();
  const {
    configuration: { basePath },
  } = useWidgetContext();
  const variantItemsWithLowerCasedKeys: IVariantListItems = {};

  const setVariantId = useCallback(
    async (variantId: string) => {
      const carId: string = Object.values(variantsList.items).find(list => list[variantId])[variantId].carId;
      dispatch(
        carConfigurationActions.setVariant({
          variantId,
          variantListItems: variantsList.items,
        }),
      );
      const { payload } = (await dispatch(
        thunkActions.selectCar({
          carId,
          configurationSourceUrl: configurationSourceUrlService.getUrl(basePath, modelId, carId, { carId }),
          priceDisplay,
        }),
      )) as any;
      if (payload) {
        dispatch(customiseActions.setSelectedSection(CustomiseSections.VARIANT));
        // @TODO: Refactor when deal decoupled from basket.
        dispatch(carConfigurationActions.setDataFromDeal(payload.deal));
      }
    },
    [dispatch, variantsList, modelId, basePath],
  );

  if (variantsListError) {
    return <ThemedErrorMessage message={'spc.common.error.general'} error={variantsListError} />;
  }

  if (variantsItems) {
    Object.keys(variantsItems).forEach((key: string) => {
      variantItemsWithLowerCasedKeys[key.toLowerCase()] = variantsItems[key];
    });
  }

  if (
    isLoading ||
    !variantsList ||
    !selectedMarketingCategory ||
    !variantItemsWithLowerCasedKeys[selectedMarketingCategory.toLowerCase()]
  ) {
    return <SectionLoader />;
  }

  return (
    <VariantList
      variants={variantItemsWithLowerCasedKeys[selectedMarketingCategory.toLowerCase()]}
      model={model}
      selectedVariantId={selectedVariantId}
      selectVariantId={setVariantId}
      detailsSlot={<SpecificationsAndFeaturesContainer />}
    />
  );
};

export default VariantListContainer;
